/**
 * World 9 — "Pale Coast" (Water). Narrative fragment for campaignStories.ts.
 *
 * THE TALE (Act II, chapter 4): the whole world is fading — every trial here
 * is half-forgotten, gray, waiting to be reminded who it is. Timmy returns at
 * the gate, packed, dry-eyed: he is getting half of Chirpy's name back. Mimi
 * takes the breather node — teaching a fading soul to battle again, the first
 * laugh since the Vault — before Lulu, split furthest from her twin, fades to
 * gray and answers to nothing; Mimi names her back, starting from the
 * half-biscuit wrapper. Aggie mutters the island's names over and over, holding
 * the door against a fading she once caused. The player's own Shard Book blurs; the
 * party carries the carrier. Fade, the Pale Guardian, is won by standing watch
 * through the white night, not by the battle alone — third First-Soul shard,
 * seventeen of nineteen. That night, unprompted, the cast gives the Keeper
 * small things.
 */

import type { NodeStory } from '../../campaignStories.js';

export const WORLD9_TAGLINE =
  "Gray dust blows along the coast. Footprints fill in, voices go quiet, and the Shard Book's ink fades. Keep your friends talking. Keep moving.";

export const WORLD9_ZONE_STORIES: Record<string, string> = {
  z24: "Timmy marches ahead, bag packed. 'I'm getting Chirpy's name back.' You hurry after him, following Gray's fading footprints.",
  z25: "The Shard Book blurs in your hands. Your friends remind you where you've been as you search the white shore.",
  z26: "Zeck has one cage left. Beyond it waits the last patch of colour on the coast.",
};

export const WORLD9_NODE_STORIES: Record<string, NodeStory> = {
  // --- Zone 24: Bleached Shore (1v1) ---
  "z24-n1": {
    story: "In front of you, an old fisherman stares at his hands. They are turning as white as his boat.",
    dialogue: {
      intro: "I used to fish here. Can't remember what I caught. A battle might bring something back.",
      playerWin: "Blue. The sea was blue. I can see it again.",
      playerLose: "Gone again. Was the sea ever blue?",
    },
  },
  "z24-n2": {
    story: "A child draws in the sand at your feet. Every line vanishes.",
    dialogue: {
      intro: "My name keeps slipping away. Battle me, then help me write it down?",
      playerWin: "You'll remember it too? Good. Now two of us know it.",
      playerLose: "Names are hard to hold. Stay and rest with me.",
    },
  },
  "z24-n3": {
    story: "You find Mimi teaching her fading Yeti the battle moves it forgot.",
    dialogue: {
      intro: "My student remembers three moves and the victory dance. Let's give them a go!",
      playerWin: "No fair! Wait, my student's laughing. Oh. That's better than winning.",
      playerLose: "We win! Pay up, Keeper!",
      introRematchAfterLoss: "Back for more? My student remembers the steps now. Mostly!",
      introRematchAfterWin: "I taught it the bow AND the dance. Watch this!",
      playerWinRepeat: "You win again! First laugh since the Vault, and it won't stop!",
      playerLoseRepeat: "We win again! Laughing is good practice, turns out.",
    },
  },
  "z24-n5": {
    story: "Zip blocks the shore with his cages. Up close, you see his jacket going gray with dust.",
    dialogue: {
      intro: "Dash stands taller than the dunes now! I have to climb him to see the sea! All three of you, get 'em!",
      playerIntro: "Climb down, Zip! Your jacket's fading. Let's get you out of here.",
      playerWin: "Retreat! Follow my jacket! ...Dash, what colour is my jacket?",
      playerLose: "Ha! Too slow! Clan Z wins the white shore!",
      introRematchAfterLoss: "Back on my beach? Dash is warmed up!",
      introRematchAfterWin: "Rematch! Finally, a face I know! Dash, go!",
      playerWinRepeat: "Fine! Keep the white shore! Dash, run!",
      playerLoseRepeat: "Slowpoke! Clan Z holds the coast!",
    },
  },
  "z24-nw1": {
    story: "Something faded rises from the foam in front of you.",
    dialogue: {
      intro: "A wild Suimon surges from the surf! It's gray with dust, and its splash is ice cold!",
      playerWin: "The wild Suimon slips back beneath the pale foam.",
      playerLose: "The wild Suimon drives you back up the shore.",
      introRematchAfterLoss: "The foam churns. The wild Suimon returns.",
      introRematchAfterWin: "The Suimon knows your scent now. It rears.",
      playerWinRepeat: "The wild Suimon sinks into the white surf.",
      playerLoseRepeat: "The Suimon holds the shore. The shallows stay wild.",
    },
  },
  "z24-n4": {
    story: "A gray wave rolls in toward you. Everything it touches turns white and cold.",
    dialogue: {
      intro: "Stay still. Let the gray dust settle on you, boots to hair.",
      playerWin: "The wave breaks around your team. A strip of coloured sand remains ahead.",
      playerLose: "There. All white. Doesn't that feel light?",
    },
  },

  // --- Zone 25: White Silence (2v2) ---
  "z25-n1": {
    story: "A singer turns to you and tries to sing. Nothing comes out.",
    dialogue: {
      intro: "I remember singing. I open my mouth to sing, and only a croak comes out. Let's try something I still know.",
      playerWin: "Did you hear that? One note. It's a start.",
      playerLose: "Let it stay quiet. Songs just hurt.",
    },
  },
  "z25-n2": {
    story: "You find Lulu standing gray and silent. Mimi calls her name. Lulu doesn't turn around.",
    dialogue: {
      intro: "Lulu? Is that me? I don't... I can still battle. Let's do that.",
      playerWin: "...You win. That wrapper. One fare, one biscuit. I'm Lulu. And Mimi took the bigger half.",
      playerLose: "See? No one. Told you.",
      introRematchAfterLoss: "You again. Still calling a stranger by a name.",
      introRematchAfterWin: "I know who I am. I know who you are. Now let's see who wins.",
      playerWinRepeat: "...You win. I'm still Lulu, and that's good.",
      playerLoseRepeat: "I won. And I remember who I want to tell.",
    },
  },
  "z25-n3": {
    story: "A mapmaker draws your path for you. The ink vanishes. She draws again.",
    dialogue: {
      intro: "The path disappears behind my pen. Let's battle before we both forget where we're going.",
      playerWin: "That line stayed. Follow it. I'll keep drawing.",
      playerLose: "The line faded. Go back while you can.",
    },
  },
  "z25-n5": {
    story: "In the fog above you, Shade circles silently. Zap waits below.",
    dialogue: {
      intro: "Shade's shadow swallows the fog bank. I can hardly see him. He can see you.",
      playerIntro: "Good. Let him see me coming.",
      playerWin: "Zeck's ahead with the last cage. You're too late.",
      playerLose: "Quiet wins. Clan Z keeps the Silence.",
      introRematchAfterLoss: "Back in the hush. Shade watched you.",
      introRematchAfterWin: "You again. Even in fog, I know that team.",
      playerWinRepeat: "...Zeck is just ahead. He won't go easy on you.",
      playerLoseRepeat: "The white stays shut. Clan Z holds.",
    },
  },
  "z25-nw2": {
    story: "Something pale drifts out of the fog toward you.",
    dialogue: {
      intro: "A wild Fernail glides from the fog! Another shape waits behind it!",
      playerWin: "The wild Fernail fades back into the white.",
      playerLose: "The wild Fernail drives you into the hush.",
      introRematchAfterLoss: "The fog stirs. The wild Fernail returns.",
      introRematchAfterWin: "The Fernail knows your scent now. It circles.",
      playerWinRepeat: "The wild Fernail slips off into the silence.",
      playerLoseRepeat: "The Fernail holds the fog. The path stays wild.",
    },
  },
  "z25-n4": {
    story: "You hear Timmy call Chirpy by her whole name. She hops closer.",
    dialogue: {
      intro: "Chirpy! I said it! The whole name! Come on, she wants to hear me cheer!",
      playerWin: "I lost! Chirpy, I lost! Ha! I can say her name as much as I want!",
      playerLose: "I won! That's ten wins! Wait, two!",
      introRematchAfterLoss: "Back again? Chirpy's name is holding. So am I!",
      introRematchAfterWin: "Chirpy says rematch. I heard her perfectly. Ready?",
      playerWinRepeat: "Another loss. Still Chirpy. Still us. I'll take it.",
      playerLoseRepeat: "I won! The name stays kept either way!",
    },
  },

  // --- Zone 26: Pale Heart (3v3) ---
  "z26-n1": {
    story: "On the white hill ahead of you, Zeck holds his last cage open toward the colour. Storm fills the sky behind him.",
    dialogue: {
      intro: "Storm tore the fog open. That last little light is mine. Clan Z will be remembered!",
      playerIntro: "You will be, Zeck. Not like this.",
      playerWin: "Storm, no! Come home! Forget the cage. I can't lose you too.",
      playerLose: "Storm wins. Clan Z takes it all.",
      introRematchAfterLoss: "Back? Storm still guards the last cage.",
      introRematchAfterWin: "You broke my whole plan. Why come back?",
      playerWinRepeat: "Storm's done. My last cage is empty. Again.",
      playerLoseRepeat: "Clan Z holds. But no one stays.",
    },
  },
  "z26-n2": {
    story: "Aggie walks beside you, saying every name she knows. She won't let one slip.",
    dialogue: {
      intro: "Mossheart. Tidebell. Cinder. Say them with me while we battle. I won't lose another name.",
      playerWin: "Well done, child! Mossheart. Tidebell. Cinder. Keep saying them with me. We haven't lost them yet.",
      playerLose: "Rest, child. The names will wait.",
      introRematchAfterLoss: "Back again? Good. The names still hold, and so do I.",
      introRematchAfterWin: "I remember all of them today. Let's give your team a match worth remembering too.",
      playerWinRepeat: "Well fought. Keep talking to your friends as you go.",
      playerLoseRepeat: "I held, child. Rest, then try once more.",
    },
  },
  "z26-n3": {
    story: "The coast's last little light flickers in front of you.",
    dialogue: {
      intro: "I'm trying to stay bright. Help me. One good battle, while I still can.",
      playerWin: "The little light glows steadily. A pink Soul Shard lands in your Shard Book. That's sixteen!",
      playerLose: "Still flickering... Come back soon. I'll stay bright for you!",
    },
  },
  "z26-n4": {
    story: "Beside you, Lulu is going pale again. Mimi grabs her hand. Up on the hill, Fade, a huge pale Yeti, keeps its lonely watch.",
    dialogue: {
      intro: "This is Fade. It kept the coast's colours safe. Now gray dust is covering it too. Stay close to your team.",
      playerWin: "You stay by Fade until the gray dust slides off. Lulu blinks. 'Mimi. You're the loud one.' Fade's shard makes seventeen! That night, by the fire, your friends leave gifts by your Shard Book. A wrapper, a pebble, a feather.",
      playerLose: "The white closes in. Hold on. Come back.",
      introRematchAfterLoss: "Fade flickers low. It needs someone to stay. You stay.",
      introRematchAfterWin: "It stays bright now. Your friends gave you small gifts that night, remember?",
      playerWinRepeat: "Won again! The white night feels shorter each time.",
      playerLoseRepeat: "Gray dust settles on Fade again. Rest up. It still needs you to stay.",
    },
  },
};
